import { PostWithDetails } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { AdBanner, EmptyState, LoadingSpinner, PatternBackground, PostCard } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { useMemo } from 'react';
import { FlatList, Platform, RefreshControl, ScrollView, useWindowDimensions, View } from 'react-native';
import { createStyles } from './FeedScreen.styles';

type FeedItem =
  | { type: 'post'; data: PostWithDetails }
  | { type: 'ad'; id: string };

const AD_INTERVAL = 5;

export interface FeedScreenProps {
  posts: PostWithDetails[];
  loading: boolean;
  loadingMore: boolean;
  currentUserId?: string;
  onRefresh: () => void;
  onLoadMore: () => void;
  onUserPress: (userId: string) => void;
  onTournamentPress: (tournamentId: string) => void;
  onDeletePost: (postId: string) => void;
  onLikePress: (postId: string, isLiked: boolean) => void;
  onSharePost?: (postId: string) => void;
  colors: {
    background: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
    divider: string;
  };
}

export const FeedScreen = ({
  posts,
  loading,
  loadingMore,
  currentUserId,
  onRefresh,
  onLoadMore,
  onUserPress,
  onTournamentPress,
  onDeletePost,
  onLikePress,
  onSharePost,
  colors,
}: FeedScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();
  const { width } = useWindowDimensions();

  // On web with large screens (>= 900px), use 2 columns for better layout
  const isLargeWeb = Platform.OS === 'web' && width >= 900;

  // For large web, split posts into 2 columns manually for better distribution
  const { leftColumn, rightColumn } = useMemo(() => {
    if (!isLargeWeb) return { leftColumn: [], rightColumn: [] };

    const left: PostWithDetails[] = [];
    const right: PostWithDetails[] = [];

    posts.forEach((post, index) => {
      if (index % 2 === 0) {
        left.push(post);
      } else {
        right.push(post);
      }
    });

    return { leftColumn: left, rightColumn: right };
  }, [posts, isLargeWeb]);

  const feedItems: FeedItem[] = useMemo(() => {
    const items: FeedItem[] = [];
    posts.forEach((post, index) => {
      items.push({ type: 'post', data: post });
      if ((index + 1) % AD_INTERVAL === 0) {
        items.push({ type: 'ad', id: `ad-${index}` });
      }
    });
    return items;
  }, [posts]);

  const renderFeedItem = ({ item }: { item: FeedItem }) => {
    if (item.type === 'ad') {
      return <AdBanner colors={colors} />;
    }
    return (
      <PostCard
        post={item.data}
        currentUserId={currentUserId}
        onUserPress={onUserPress}
        onTournamentPress={onTournamentPress}
        onDeletePress={onDeletePost}
        onLikePress={onLikePress}
        onSharePress={onSharePost}
        colors={colors}
      />
    );
  };

  // Render 2-column masonry layout for large web screens
  if (isLargeWeb && !loading) {
    return (
      <PatternBackground>
        <ScrollView
          contentContainerStyle={styles.list}
          onScroll={({ nativeEvent }) => {
            const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
            const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 200;
            if (isCloseToBottom && !loadingMore) {
              onLoadMore();
            }
          }}
          scrollEventThrottle={400}
          refreshControl={
            <RefreshControl refreshing={loading} onRefresh={onRefresh} tintColor={colors.primary} />
          }
        >
          {posts.length === 0 ? (
            <EmptyState
              icon={<Ionicons name="newspaper-outline" size={52} color={colors.textTertiary} />}
              title={t('feed.noPostsYet')}
              subtitle={t('feed.followUsersToSeePosts')}
              colors={colors}
            />
          ) : (
            <View style={styles.columnsContainer}>
              {/* Left Column */}
              <View style={styles.column}>
                {leftColumn.map((post, index) => (
                  <View key={post.id}>
                    <View style={styles.postWrapper}>
                      <PostCard
                        post={post}
                        currentUserId={currentUserId}
                        onUserPress={onUserPress}
                        onTournamentPress={onTournamentPress}
                        onDeletePress={onDeletePost}
                        onLikePress={onLikePress}
                        onSharePress={onSharePost}
                        colors={colors}
                      />
                    </View>
                    {index % 3 === 2 && <AdBanner colors={colors} />}
                  </View>
                ))}
              </View>

              {/* Right Column */}
              <View style={styles.column}>
                {rightColumn.map((post, index) => (
                  <View key={post.id}>
                    <View style={styles.postWrapper}>
                      <PostCard
                        post={post}
                        currentUserId={currentUserId}
                        onUserPress={onUserPress}
                        onTournamentPress={onTournamentPress}
                        onDeletePress={onDeletePost}
                        onLikePress={onLikePress}
                        onSharePress={onSharePost}
                        colors={colors}
                      />
                    </View>
                    {index % 3 === 2 && <AdBanner colors={colors} />}
                  </View>
                ))}
              </View>
            </View>
          )}

          {loadingMore && <LoadingSpinner color={colors.primary} backgroundColor={colors.background} />}
        </ScrollView>
      </PatternBackground>
    );
  }

  // Standard single-column FlatList for mobile and small screens
  return (
    <PatternBackground>
      {loading ? (
        <LoadingSpinner color={colors.primary} backgroundColor={colors.background} />
      ) : (
        <FlatList
          data={feedItems}
          renderItem={renderFeedItem}
          keyExtractor={(item) => (item.type === 'ad' ? item.id : item.data.id)}
          contentContainerStyle={styles.list}
          onEndReached={onLoadMore}
          onEndReachedThreshold={0.5}
          refreshControl={
            <RefreshControl refreshing={loading} onRefresh={onRefresh} tintColor={colors.primary} />
          }
          ListFooterComponent={
            loadingMore ? (
              <LoadingSpinner color={colors.primary} backgroundColor={colors.background} />
            ) : null
          }
          ListEmptyComponent={
            <EmptyState
              icon={<Ionicons name="newspaper-outline" size={52} color={colors.textTertiary} />}
              title={t('feed.noPostsYet')}
              subtitle={t('feed.followUsersToSeePosts')}
              colors={colors}
            />
          }
        />
      )}
    </PatternBackground>
  );
};
